// material
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import { UserListHead } from '../sections/@dashboard/user';
import { Search, SearchIconWrapper, StyledInputBase } from '../components/SearchInput';

// store
import { api as stockApi } from '../store/domains/stock';
import { useDebounce } from '../utils/debounce';

const TABLE_HEAD = [
  { id: 'pinCode', label: 'FIN Code', alignRight: false },
  { id: 'esim', label: 'eSIM', alignRight: false },
  { id: 'physicalSim', label: 'Physical SIM', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'createdAt', label: 'Created', alignRight: false },
  { id: 'updatedAt', label: 'Updated', alignRight: false },
  { id: 'actions', label: '', alignRight: false },
];

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

const STATUS_COLOR = { ACTIVE: 'success', INACTIVE: 'default', DELETED: 'error' };

const MySwal = withReactContent(Swal);

// Backend LocalDateTime → "2026-07-23 10:15:30"
function formatDate(value) {
  if (!value) return '—';
  return value.replace('T', ' ').split('.')[0];
}

function yesNo(value) {
  return value ? 'Yes' : 'No';
}

export default function Whitelist() {
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPinCode, setNewPinCode] = useState('');
  const [newEsim, setNewEsim] = useState(false);
  const [newPhysicalSim, setNewPhysicalSim] = useState(false);

  const debouncedSearch = useDebounce(searchValue, 400);

  const { data, isLoading, isFetching } = stockApi.useGetWhitelist();

  const [addWhitelist, { isLoading: addLoading }] = stockApi.useAddWhitelist();
  const [activateWhitelist, { isLoading: activateLoading }] = stockApi.useActivateWhitelist();
  const [deactivateWhitelist, { isLoading: deactivateLoading }] = stockApi.useDeactivateWhitelist();
  const [deleteWhitelist, { isLoading: deleteLoading }] = stockApi.useDeleteWhitelist();

  const mutating = addLoading || activateLoading || deactivateLoading || deleteLoading;

  // The API returns the full list (DELETED excluded); search + status filter are client-side.
  const filteredRows = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const term = debouncedSearch.trim().toUpperCase();
    return rows.filter((row) => {
      const matchesSearch = !term || (row.pinCode ?? '').toUpperCase().includes(term);
      const matchesStatus = statusFilter === 'ALL' || row.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, debouncedSearch, statusFilter]);

  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  // Keep the page in range when the list shrinks (e.g. after a delete/deactivate
  // removes the only row on the last page).
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
    setPage((prev) => Math.min(prev, lastPage));
  }, [filteredRows.length, rowsPerPage]);

  const handleChangeSearchInput = (event) => {
    setSearchValue(event.target.value);
    setPage(0);
  };

  const handleChangeStatusFilter = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenCreateDialog = () => setCreateDialogOpen(true);

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewPinCode('');
    setNewEsim(false);
    setNewPhysicalSim(false);
  };

  const handleCreateSubmit = async () => {
    const pinCode = newPinCode.trim();
    if (!pinCode) return;
    try {
      await addWhitelist({ pinCode, esim: newEsim, physicalSim: newPhysicalSim }).unwrap();
      handleCloseCreateDialog();
      MySwal.fire('Success', 'FIN code added to whitelist', 'success');
    } catch {
      // Global axios interceptor already shows the error modal.
      handleCloseCreateDialog();
    }
  };

  const handleToggleStatus = async (row) => {
    try {
      if (row.status === 'ACTIVE') {
        await deactivateWhitelist(row.pinCode).unwrap();
      } else {
        await activateWhitelist(row.pinCode).unwrap();
      }
    } catch {
      // Handled by global interceptor.
    }
  };

  const handleDelete = async (row) => {
    const result = await MySwal.fire({
      title: 'Remove from whitelist?',
      text: `FIN code ${row.pinCode} will be removed from the whitelist.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
      confirmButtonColor: '#d33',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteWhitelist(row.pinCode).unwrap();
      MySwal.fire('Removed', 'FIN code removed from whitelist', 'success');
    } catch {
      // Handled by global interceptor.
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Page>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              onChange={handleChangeSearchInput}
              value={searchValue}
              placeholder="Search FIN code…"
              inputProps={{ 'aria-label': 'search' }}
            />
          </Search>

          <Button onClick={handleOpenCreateDialog} sx={{ width: '200px', maxHeight: '56px' }} variant="contained">
            Add FIN Code
          </Button>

          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={handleChangeStatusFilter}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Card>
          {isFetching && <LinearProgress />}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead headLabel={TABLE_HEAD} rowCount={paginatedRows.length} />
                <TableBody>
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((row) => {
                      const { id, pinCode, esim, physicalSim, status, createdAt, updatedAt } = row;
                      const isActive = status === 'ACTIVE';
                      return (
                        <TableRow hover key={id ?? pinCode} tabIndex={-1}>
                          <TableCell padding="checkbox" />
                          <TableCell align="left">{pinCode}</TableCell>
                          <TableCell align="left">{yesNo(esim)}</TableCell>
                          <TableCell align="left">{yesNo(physicalSim)}</TableCell>
                          <TableCell align="left">
                            <Chip label={status} color={STATUS_COLOR[status] ?? 'default'} size="small" />
                          </TableCell>
                          <TableCell align="left">{formatDate(createdAt)}</TableCell>
                          <TableCell align="left">{formatDate(updatedAt)}</TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Button
                                size="small"
                                variant="outlined"
                                color={isActive ? 'warning' : 'success'}
                                onClick={() => handleToggleStatus(row)}
                                disabled={mutating}
                              >
                                {isActive ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => handleDelete(row)}
                                disabled={mutating}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={TABLE_HEAD.length + 1} align="center">
                        No results
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Add FIN Code to Whitelist</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                label="FIN Code"
                variant="outlined"
                size="small"
                fullWidth
                value={newPinCode}
                onChange={(e) => setNewPinCode(e.target.value)}
              />
              <FormControlLabel
                control={<Checkbox checked={newEsim} onChange={(e) => setNewEsim(e.target.checked)} />}
                label="eSIM"
              />
              <FormControlLabel
                control={
                  <Checkbox checked={newPhysicalSim} onChange={(e) => setNewPhysicalSim(e.target.checked)} />
                }
                label="Physical SIM"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog} disabled={addLoading}>
              Cancel
            </Button>
            <Button onClick={handleCreateSubmit} variant="contained" disabled={addLoading || !newPinCode.trim()}>
              {addLoading ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
